import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  useTheme
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

interface SystemComponent {
  name: string;
  status: 'healthy' | 'warning' | 'error';
  responseTime?: string;
}

interface SystemStatusCardProps {
  systemStatus: 'Online' | 'Degraded' | 'Offline';
  components: SystemComponent[];
  lastUpdated?: Date;
}

export const SystemStatusCard: React.FC<SystemStatusCardProps> = ({
  systemStatus,
  components,
  lastUpdated
}) => {
  const theme = useTheme();
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Online':
      case 'healthy':
        return theme.palette.success.main;
      case 'Degraded':
      case 'warning':
        return theme.palette.warning.main;
      case 'Offline':
      case 'error':
        return theme.palette.error.main;
      default:
        return theme.palette.info.main;
    }
  };
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
      case 'warning':
        return <WarningIcon sx={{ color: theme.palette.warning.main }} />;
      case 'error':
        return <ErrorIcon sx={{ color: theme.palette.error.main }} />;
      default:
        return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
    }
  };
  
  return (
    <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">System Status</Typography>
          <Chip 
            label={systemStatus} 
            sx={{ 
              bgcolor: getStatusColor(systemStatus),
              color: 'white',
              fontWeight: 'medium'
            }} 
          />
        </Box>
        
        {lastUpdated && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Last updated: {lastUpdated.toLocaleTimeString()}
          </Typography>
        )}
        
        <Divider sx={{ mb: 2 }} />
        
        <List sx={{ py: 0 }}>
          {components.map((component, index) => (
            <React.Fragment key={component.name}>
              <ListItem sx={{ py: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {getStatusIcon(component.status)}
                </ListItemIcon>
                <ListItemText 
                  primary={component.name}
                  secondary={component.responseTime ? `Response Time: ${component.responseTime}` : undefined}
                  primaryTypographyProps={{ fontWeight: 'medium' }}
                />
              </ListItem>
              {index < components.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};
